import React, { FC, useEffect, useRef, useState } from 'react';

import { PlayCircle } from '@mui/icons-material';
import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import { KeyboardKey, RouteNavMatch } from '~/core/enums';
import { sendGoalDetailVideoStartEvent } from '~/core/googleAnalyticsUtils';
import { CoreGoal, Team } from '~/core/types';
import VideoPlayer from '~/views/components/videoPlayer/VideoPlayer';

import { useGoalDiscreteClip, useGoalHighlightClip, useGoalSharingUrl } from './utils';

import { PlayButtonContainer } from './styles';

type Props = {
    homeTeam: Team;
    awayTeam: Team;
    goal: CoreGoal;
    gameState: ShortCodeEnums.GameState | null;
    pageName?: RouteNavMatch.SCORES | RouteNavMatch.GAMECENTER;
};

const VideoClipPlayer: FC<Props> = ({ homeTeam, awayTeam, goal, gameState, pageName = RouteNavMatch.GAMECENTER }) => {
    const [showVideo, setShowVideo] = useState(false);
    const [hasVideoOpened, setHasVideoOpened] = useState(false);
    const playButtonRef = useRef<HTMLAnchorElement>(null);
    const isScores = pageName === RouteNavMatch.SCORES;

    const discreteClip = useGoalDiscreteClip(goal);
    const highlightClip = useGoalHighlightClip(goal);
    const sharingUrl = useGoalSharingUrl(goal);
    const videoClip = isScores ? discreteClip : highlightClip;

    const handleKeyPress = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === KeyboardKey.SPACE || e.key === KeyboardKey.ENTER) {
            openVideo();
        }
    };

    const openVideo = () => {
        if (videoClip !== undefined) {
            sendGoalDetailVideoStartEvent(
                videoClip,
                gameState,
                pageName,
                `${goal.firstName.default} ${goal.lastName.default}`,
                homeTeam.abbrev,
                awayTeam.abbrev
            );
            setShowVideo(true);
            setHasVideoOpened(true);
        }
    };

    useEffect(() => {
        if (hasVideoOpened && !showVideo && playButtonRef.current) {
            playButtonRef.current?.focus();
        }
    }, [showVideo, hasVideoOpened]);

    return (
        <>
            <PlayButtonContainer
                ref={playButtonRef}
                tabIndex={!videoClip ? -1 : 0}
                $hidden={!videoClip}
                onClick={openVideo}
                onKeyDown={handleKeyPress}
                $isScores={isScores}
            >
                <PlayCircle />
            </PlayButtonContainer>

            {showVideo && videoClip && (
                <VideoPlayer videoId={videoClip} sharingUrl={sharingUrl} closePlayer={() => setShowVideo(false)} />
            )}
        </>
    );
};

export default VideoClipPlayer;
